import JSZip from 'jszip';
import { getMenu } from '@/lib/firebase/menus';
import { getImageMenuItems } from '@/lib/firebase/imageMenus';
import type { 
  Restaurant, 
  MenuSection, 
  MenuItem, 
  ImageMenuItem 
} from '@/types';

export interface ExportData {
  restaurant: Omit<Restaurant, 'id' | 'ownerId'>;
  digitalMenu: {
    sections: Array<Omit<MenuSection, 'id' | 'restaurantId'>>;
    items: Array<Omit<MenuItem, 'id' | 'restaurantId' | 'sectionId'> & { sectionOrder: number }>;
  };
  imageMenu: Array<Omit<ImageMenuItem, 'id' | 'restaurantId'>>;
  metadata: {
    exportedAt: string;
    version: '1.0';
    type: 'digital-menu-export';
    exportedBy: string;
  };
}

export interface ExportProgress {
  stage: 'collecting' | 'downloading_images' | 'creating_zip' | 'complete';
  message: string;
  progress: number; // 0-100
  currentItem?: string;
}

/**
 * Download a file from URL as blob
 */
// eslint-disable-next-line no-undef
async function downloadImageAsBlob(url: string): Promise<Blob | null> {
  try {
    // Try multiple approaches for different environments
    
    // Approach 1: Direct fetch (works in production)
    try {
      // eslint-disable-next-line no-undef
      const response = await fetch(url, {
        method: 'GET',
        mode: 'cors'
      });
      
      if (response.ok) {
        const contentType = response.headers.get('content-type');
        if (contentType?.startsWith('image/')) {
          return await response.blob();
        }
      }
    } catch (error) {
      console.warn('Direct fetch failed, trying alternative approach:', error);
    }

    // Approach 2: Create image element and convert to canvas (fallback for CORS issues)
    return new Promise((resolve) => {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      
      img.onload = () => {
        try {
          const canvas = document.createElement('canvas');
          const ctx = canvas.getContext('2d');
          
          if (!ctx) {
            resolve(null);
            return;
          }

          canvas.width = img.width;
          canvas.height = img.height;
          ctx.drawImage(img, 0, 0);
          
          canvas.toBlob((blob) => {
            resolve(blob);
          }, 'image/webp', 0.8);
        } catch (error) {
          console.warn('Canvas conversion failed:', error);
          resolve(null);
        }
      };
      
      img.onerror = () => {
        console.warn(`Failed to load image: ${url}`);
        resolve(null);
      };
      
      // Add timestamp to prevent caching issues
      const separator = url.includes('?') ? '&' : '?';
      img.src = `${url}${separator}_t=${Date.now()}`;
    });
    
  } catch (error) {
    console.warn(`Error downloading image: ${url}`, error);
    return null;
  }
}

/**
 * Extract filename from Firebase Storage URL
 */
function extractFilenameFromUrl(url: string): string {
  try {
    // Firebase Storage URLs contain the full path
    const urlParts = url.split('/o/')[1];
    if (!urlParts) return 'unknown.webp';
    
    const pathWithParams = urlParts.split('?')[0];
    const decodedPath = decodeURIComponent(pathWithParams);
    const pathSegments = decodedPath.split('/');
    return pathSegments[pathSegments.length - 1];
  } catch (error) {
    console.warn('Error extracting filename from URL:', error);
    return 'unknown.webp';
  }
}

/**
 * Export complete menu data and images as ZIP file
 */
export async function exportMenu(
  restaurant: Restaurant,
  onProgress?: (progress: ExportProgress) => void
): Promise<void> {
  const zip = new JSZip();
  
  try {
    // Stage 1: Collect menu data
    onProgress?.({
      stage: 'collecting',
      message: 'Collecting menu data...',
      progress: 10
    });

    // Fetch digital menu data
    const menuData = await getMenu(restaurant.id);
    if (!menuData) {
      throw new Error('No menu data found for this restaurant');
    }

    // Fetch image menu data
    const imageMenuItems = await getImageMenuItems(restaurant.id);

    // Check if there's any data to export
    const hasDigitalMenuData = menuData.sections.length > 0 || menuData.items.length > 0;
    const hasImageMenuData = imageMenuItems.length > 0;
    const hasRestaurantLogo = !!restaurant.logoUrl;

    if (!hasDigitalMenuData && !hasImageMenuData && !hasRestaurantLogo) {
      throw new Error('No content to export. Please add menu items or images first.');
    }

    // Stage 2: Download images
    onProgress?.({
      stage: 'downloading_images',
      message: 'Downloading images...',
      progress: 20
    });

    const allImages: Array<{ url: string; path: string }> = [];

    // Add restaurant logo if exists
    if (restaurant.logoUrl) {
      allImages.push({
        url: restaurant.logoUrl,
        path: `restaurant-logo.webp`
      });
    }

    // Add digital menu item images
    menuData.items.forEach((item) => {
      if (item.imageUrl) {
        const filename = extractFilenameFromUrl(item.imageUrl);
        allImages.push({
          url: item.imageUrl,
          path: `images/menu-items/${filename}`
        });
      }
    });

    // Add image menu items (both full and thumbnail)
    imageMenuItems.forEach((imageItem) => {
      const filename = extractFilenameFromUrl(imageItem.imageUrl);
      
      allImages.push({
        url: imageItem.imageUrl,
        path: `images/image-menu/full/${filename}`
      });

      if (imageItem.thumbnailUrl) {
        allImages.push({
          url: imageItem.thumbnailUrl,
          path: `images/image-menu/thumbnails/${filename}`
        });
      }
    });

    // Download all images
    const totalImages = allImages.length;
    
    if (totalImages === 0) {
      onProgress?.({
        stage: 'creating_zip',
        message: 'No images to download, creating export package...',
        progress: 70
      });
    } else {
      const imagePromises = allImages.map(async (image, index) => {
        onProgress?.({
          stage: 'downloading_images',
          message: `Downloading images (${index + 1}/${totalImages})...`,
          progress: 20 + (index / totalImages) * 50,
          currentItem: image.path
        });

        const blob = await downloadImageAsBlob(image.url);
        if (blob) {
          zip.file(image.path, blob);
        }
      });

      await Promise.allSettled(imagePromises); // Use allSettled to continue even if some images fail
    }

    // Stage 3: Create export data and add to ZIP
    onProgress?.({
      stage: 'creating_zip',
      message: 'Creating export package...',
      progress: 80
    });

    // Prepare export data (remove IDs and foreign keys)
    const exportData: ExportData = {
      restaurant: {
        name: restaurant.name,
        description: restaurant.description,
        phone: restaurant.phone,
        address: restaurant.address,
        slug: restaurant.slug,
        logoUrl: restaurant.logoUrl,
        coverImageUrl: restaurant.coverImageUrl,
        themeColor: restaurant.themeColor,
        isActive: restaurant.isActive,
        createdAt: restaurant.createdAt,
        updatedAt: restaurant.updatedAt
      },
      digitalMenu: {
        sections: menuData.sections.map(section => ({
          name: section.name,
          description: section.description,
          order: section.order,
          isVisible: section.isVisible,
          collapsed: section.collapsed,
          createdAt: section.createdAt,
          updatedAt: section.updatedAt
        })),
        items: menuData.items.map(item => {
          // Find section order for this item
          const section = menuData.sections.find(s => s.id === item.sectionId);
          const sectionOrder = section?.order ?? 0;

          return {
            name: item.name,
            description: item.description,
            price: item.price,
            originalPrice: item.originalPrice,
            dietType: item.dietType,
            imageUrl: item.imageUrl,
            imageMetadata: item.imageMetadata,
            ingredients: item.ingredients,
            allergens: item.allergens,
            order: item.order,
            sectionOrder, // Add section order for reconstruction
            isAvailable: item.isAvailable,
            isPopular: item.isPopular,
            isNew: item.isNew,
            preparationTime: item.preparationTime,
            calories: item.calories,
            spiceLevel: item.spiceLevel,
            isVegan: item.isVegan,
            isJain: item.isJain,
            enhancedAllergens: item.enhancedAllergens,
            enhancedSpiceLevel: item.enhancedSpiceLevel,
            cuisineTags: item.cuisineTags,
            customTags: item.customTags,
            isSignatureDish: item.isSignatureDish,
            isChefSpecial: item.isChefSpecial,
            createdAt: item.createdAt,
            updatedAt: item.updatedAt
          };
        })
      },
      imageMenu: imageMenuItems.map(imageItem => ({
        imageUrl: imageItem.imageUrl,
        thumbnailUrl: imageItem.thumbnailUrl,
        fileName: imageItem.fileName,
        fileSize: imageItem.fileSize,
        dimensions: imageItem.dimensions,
        order: imageItem.order,
        uploadedAt: imageItem.uploadedAt,
        updatedAt: imageItem.updatedAt
      })),
      metadata: {
        exportedAt: new Date().toISOString(),
        version: '1.0',
        type: 'digital-menu-export',
        exportedBy: restaurant.ownerId
      }
    };

    // Add JSON data to ZIP
    zip.file('menu-data.json', JSON.stringify(exportData, null, 2));

    // Stage 4: Generate and download ZIP
    onProgress?.({
      stage: 'creating_zip',
      message: 'Generating download...',
      progress: 95
    });

    const content = await zip.generateAsync({ type: 'blob' });
    
    // Create download
    const url = URL.createObjectURL(content);
    const link = document.createElement('a');
    link.href = url;
    
    const timestamp = new Date().toISOString().slice(0, 19).replace(/[:.]/g, '-');
    link.download = `${restaurant.slug}-menu-export-${timestamp}.zip`;
    
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);

    onProgress?.({
      stage: 'complete',
      message: 'Export completed successfully!',
      progress: 100
    });

  } catch (error) {
    console.error('Export failed:', error);
    throw new Error(error instanceof Error ? error.message : 'Export failed');
  }
}
